// Turns model citations like "(snippet 8)", "(snippets 11, 13)", "[8]", "snippet 8"
// into clickable superscript links, and collects the set of cited numbers.
import React from "react";

// Capture group 1: 'snippet(s) N (, N)*' form
// Capture group 2: '[N (, N)*]' form
const CITE_RE = /\(?snippets?\s+(\d+(?:\s*(?:,|and)\s*\d+)*)\)?|\[(\d+(?:\s*,\s*\d+)*)\]/gi;

function parseNumbers(raw) {
  return (raw || "")
    .split(/[,\s]+|and/i)
    .map((x) => x.trim())
    .filter((x) => /^\d+$/.test(x))
    .map(Number);
}

/** Walk a nested value (object / array / string) and collect every cited snippet number. */
export function collectCitedNumbers(value) {
  const nums = new Set();
  const flat = JSON.stringify(value ?? {});
  let m;
  const re = new RegExp(CITE_RE.source, CITE_RE.flags);
  while ((m = re.exec(flat)) !== null) {
    parseNumbers(m[1] || m[2] || "").forEach((n) => nums.add(n));
  }
  return [...nums].sort((a, b) => a - b);
}

/** Render `text` with citation tokens replaced by clickable superscript links.
 *  Unknown numbers (no matching snippet) are stripped rather than rendered as broken refs.
 *  Keys are stable per source-string offset (m.index) so React never conflates segments. */
export function renderCited(text, snippets) {
  if (text == null || text === "") return text ?? null;
  const s = String(text);
  const nodes = [];
  const re = new RegExp(CITE_RE.source, CITE_RE.flags);
  let last = 0;
  let m;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) {
      nodes.push({ kind: "t", offset: last, value: s.slice(last, m.index) });
    }
    const nums = parseNumbers(m[1] || m[2] || "");
    const valid = nums.filter((n) => snippets?.[n - 1]);
    if (valid.length > 0) {
      nodes.push({ kind: "c", offset: m.index, nums: valid });
    }
    last = m.index + m[0].length;
  }
  if (last < s.length) {
    nodes.push({ kind: "t", offset: last, value: s.slice(last) });
  }

  const rendered = nodes.map((node) => {
    if (node.kind === "t") return <React.Fragment key={`t-${node.offset}`}>{node.value}</React.Fragment>;
    return (
      <sup key={`c-${node.offset}`} className="inline-flex ml-0.5 gap-0.5 align-super">
        {node.nums.map((n, i) => {
          const src = snippets[n - 1];
          return (
            <a
              key={`sn-${n}`}
              href={src.url || `#src-${n}`}
              target="_blank"
              rel="noreferrer noopener"
              title={src.title || src.url}
              data-testid={`cite-${n}`}
              className="text-[10px] font-semibold text-[#1E90FF] hover:underline whitespace-nowrap"
            >
              [{n}]{i < node.nums.length - 1 ? "," : ""}
            </a>
          );
        })}
      </sup>
    );
  });

  return rendered.length === 1 ? rendered[0] : <>{rendered}</>;
}

/** Small drop-in wrapper: <Cited>{some text}</Cited> */
export function Cited({ children, snippets }) {
  if (Array.isArray(children)) {
    return (
      <>
        {children.map((c, i) => (
          <React.Fragment key={`cited-${i}-${typeof c === "string" ? c.length : i}`}>
            {renderCited(c, snippets)}
          </React.Fragment>
        ))}
      </>
    );
  }
  return <>{renderCited(children, snippets)}</>;
}
